import { useEffect, useState } from 'react';

import { measureBlocks, sameCuts } from '../components/layouts/pageBlockFit';

const NONE = Object.freeze([]);

// A change to the sheet is measured once it has settled, not at each of the dozen
// writes one render makes. A timer, not a frame: a tab in the background has no
// frames and the answer is still wanted when it comes forward.
const SETTLE_MS = 30;

/**
 * Which blocks under `root` (an element, from a callback ref, so a root that is
 * replaced is watched afresh) hold more than their box shows (KPR-1861): the answer
 * of `measureBlocks`, kept current.
 *
 * A block with auto scale (KPR-1862) is shrunk as it is measured, and what is
 * reported is what is still cut at the size it ended at. Doing so writes
 * `--fit-scale` on the block, which is a `style` change this hook watches, so the
 * records of its own writes are thrown away; otherwise it would measure itself
 * forever. The scale is left on the DOM when the hook is held (a block is carried)
 * and is what the print route prints.
 *
 * It is read from the layout the browser drew, so it is re-read whenever that can
 * change: the DOM under the root changes (a block, a value, a query's rows, a
 * box's size, which is its inline style), a font finishes loading (a sheet is
 * measured in the font it prints in, and the first reading may be in the fallback),
 * or an image arrives. `paused` holds the last answer while a block is carried.
 * `enabled` false is no blocks: a plain layout never observes anything.
 */
export default function useBlockFit(root, { enabled = true, paused = false } = {}) {
	const [cuts, setCuts] = useState(NONE);

	useEffect(() => {
		if (!enabled) { setCuts(NONE); return undefined; }
		if (paused || !root) return undefined;

		let timer = null;
		let live = true;
		let observer = null;
		const measure = () => {
			timer = null;
			if (!live) return;
			const next = measureBlocks(root);
			// The writes of auto scale are ours: not a change to settle on.
			if (observer) observer.takeRecords();
			// No size to measure against (hidden for now): the last answer stands.
			if (next === null) return;
			setCuts(previous => (sameCuts(previous, next) ? previous : (next.length ? next : NONE)));
		};
		const settle = () => {
			if (timer === null && live) timer = setTimeout(measure, SETTLE_MS);
		};

		observer = new MutationObserver(settle);
		measure();
		observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style', 'data-fit-name', 'data-autoscale', 'data-not-shown'] });
		// An image arriving does not change the DOM, only its box.
		root.addEventListener('load', settle, true);
		// A root that was hidden and is shown has changed nothing in the DOM, only its size.
		const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(settle) : null;
		if (resize) resize.observe(root);
		const fonts = typeof document !== 'undefined' ? document.fonts : null;
		if (fonts?.addEventListener) fonts.addEventListener('loadingdone', settle);
		if (fonts?.ready?.then) fonts.ready.then(settle);

		return () => {
			live = false;
			clearTimeout(timer);
			observer.disconnect();
			if (resize) resize.disconnect();
			root.removeEventListener('load', settle, true);
			if (fonts?.removeEventListener) fonts.removeEventListener('loadingdone', settle);
		};
	}, [root, enabled, paused]);

	return cuts;
}
